import { useCallback, useMemo, useState } from "react";
import { Button, Flex, Text } from "@radix-ui/themes";
import { Dialog } from "@/components/shared/dialog";
import { useGetSimulatorById } from "@/api/useRelease";
import Select from "@/components/shared/select";
import { useRegressionApi } from "@/v2/entities/regression/Regression.api";
import { useMutation } from "react-query";

interface RegressionTestsEmbeddableProps {
  releaseId: number;
  simulatorId: number;
}

export function RegressionTestsEmbeddable({
  releaseId,
  simulatorId,
}: RegressionTestsEmbeddableProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedReleaseId, setSelectedReleaseId] = useState<string>("");
  const [isRunning, setIsRunning] = useState(false);
  const [testResult, setTestResult] = useState<{
    regressionTestId: string;
    showSuccess: boolean;
  } | null>(null);
  const { data: simulator, isLoading } = useGetSimulatorById(true, simulatorId);

  const { runRegressionTestByTargetRelease } = useRegressionApi();

  const { mutateAsync } = useMutation({
    mutationKey: ["run-regression-test-by-target-release"],
    mutationFn: runRegressionTestByTargetRelease,
  });

  const availableReleases = useMemo(() => {
    if (!simulator?.releases || !Array.isArray(simulator?.releases)) return [];
    return simulator?.releases.filter(
      (release: any) => release.id !== releaseId
    );
  }, [simulator, releaseId]);

  const handleRunTest = useCallback(async () => {
    if (!selectedReleaseId) return;

    setIsRunning(true);
    try {
      const response = await mutateAsync({
        releaseToTestId: releaseId,
        // releaseToTestAgainstId: +selectedReleaseId,
        numberOfConversations: 1,
      });

      if (response?.regressionTestId) {
        setTestResult({
          regressionTestId: response.regressionTestId,
          showSuccess: true,
        });
      }
    } catch (error) {
      console.error("Failed to run regression test:", error);
    } finally {
      setIsRunning(false);
    }
  }, [selectedReleaseId, mutateAsync, releaseId]);

  const handleOpenChange = useCallback((open: boolean) => {
    setIsOpen(open);
    if (!open) {
      setSelectedReleaseId("");
      setTestResult(null);
    }
  }, []);

  const handleOpenInNewTab = useCallback(() => {
    if (testResult?.regressionTestId) {
      window.open(
        `/automation-center/regression-tests/${testResult.regressionTestId}`,
        "_blank"
      );
    }
  }, [testResult?.regressionTestId]);

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <Dialog.Trigger>
        <Button
          size="2"
          color="gray"
          highContrast
          variant="outline"
          className="px-4 py-2 rounded-md"
        >
          Run Regression Test
        </Button>
      </Dialog.Trigger>

      <Dialog.Content size="small" className="p-4">
        <Flex className="flex justify-end absolute top-4 right-4">
          <Dialog.Close>
            <Button variant="soft" color="gray">
              Cancel
            </Button>
          </Dialog.Close>
        </Flex>
        <Dialog.Title>Run Regression Test</Dialog.Title>

        {testResult?.showSuccess ? (
          <Flex direction="column" gap="4" className="mt-4">
            <Text size="2" color="green" weight="medium">
              ✅ Regression test started successfully!
            </Text>
            <Text size="2" color="gray">
              Your test is now running. You can view the results once it
              completes.
            </Text>

            <Flex gap="3" mt="4" justify="end">
              <Button
                variant="outline"
                color="gray"
                onClick={handleOpenInNewTab}
              >
                View results
              </Button>
            </Flex>
          </Flex>
        ) : (
          <>
            <Text size="2" mb="4" mt="4">
              Select a previous release to compare against the current release.
            </Text>

            <Flex direction="column" gap="4">
              {isLoading ? (
                <Text size="2" color="gray">
                  Loading releases...
                </Text>
              ) : availableReleases.length === 0 ? (
                <Text size="2" color="gray">
                  No previous releases available for comparison.
                </Text>
              ) : (
                <>
                  <Select
                    value={selectedReleaseId}
                    onChange={(value) => setSelectedReleaseId(value)}
                    options={availableReleases
                      .sort((a: any, b: any) => a.id - b.id)
                      .map((release: any, index: number) => ({
                        label: release.name || `version ${index + 1}`,
                        value: release.id,
                      }))}
                    placeholder="Select a release to compare against"
                  ></Select>

                  <Flex gap="3" mt="4" justify="end">
                    <Button
                      variant="soft"
                      color="gray"
                      onClick={() => handleOpenChange(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      color="gray"
                      highContrast
                      onClick={handleRunTest}
                      disabled={!selectedReleaseId || isRunning}
                    >
                      {isRunning ? "Running Test..." : "Run Test"}
                    </Button>
                  </Flex>
                </>
              )}
            </Flex>
          </>
        )}
      </Dialog.Content>
    </Dialog>
  );
}
